package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMCardTitleTextStyle
import ai.wagemix.web.theme.WMClusterGapVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMTightGapVar
import ai.wagemix.web.theme.wagemix
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import com.varabyte.kobweb.compose.css.Overflow
import org.jetbrains.compose.web.dom.Div

/** The chart header: title block left, controls right; the controls wrap under the title on phones. */
val WMChartCardHeaderStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(WMClusterGapVar.value())
    }
    FSBreakpoint.Expanded {
        Modifier.flexDirection(FlexDirection.Row).justifyContent(JustifyContent.SpaceBetween).flexWrap(FlexWrap.Wrap)
    }
}

/**
 * The surface the chart sits on; its fill equals the fscharts canvas colour so the embed's
 * edges disappear (fscharts cannot be restyled from the page). The chart is inset by the corner
 * radius, so the rounded corners clip only the surface, never the embed's axis labels.
 */
val WMChartCanvasStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toPalette().wagemix.chartCanvas)
        .borderRadius(WMCardCornerVar.value())
        .padding(WMCardCornerVar.value())
        .overflow(Overflow.Hidden)
}

/**
 * Penpot `Wagemix / Chart Card`: a card with a [title] and [subtitle] ("Annual wages ·
 * California · All industries"), a [controls] slot (Annual/Hourly, projection stepper), the
 * [chart] on the canvas surface and a [footer] slot ("Source: BLS OEWS", notes).
 */
@Composable
fun WMChartCard(
    title: String,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
    controls: (@Composable () -> Unit)? = null,
    footer: (@Composable () -> Unit)? = null,
    chart: @Composable () -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    WMCard(modifier = modifier) {
        Div(attrs = WMChartCardHeaderStyle.toModifier().toAttrs()) {
            Column(modifier = Modifier.gap(WMTightGapVar.value())) {
                SpanText(title, WMCardTitleTextStyle.toModifier())
                subtitle?.let { SpanText(it, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant)) }
            }
            controls?.let { Row(modifier = Modifier.gap(WMClusterGapVar.value()), verticalAlignment = Alignment.CenterVertically) { it() } }
        }
        Div(attrs = WMChartCanvasStyle.toModifier().toAttrs()) { chart() }
        footer?.let { Column(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value())) { it() } }
    }
}

/** A source caption under a chart ("Source: BLS OEWS"). */
@Composable
fun WMChartSource(text: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    SpanText(text, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant).then(modifier))
}
